import React, { useState, useEffect } from 'react';
import FlipAnalyzerMockup from './mockups/FlipAnalyzerMockup';
import DashboardMockup from './mockups/DashboardMockup';
import KanbanMockup from './mockups/KanbanMockup';

const previews = [
  {
    name: 'Dashboard',
    url: 'app.flipperpro.com/dashboard',
    Component: DashboardMockup,
    annotations: [
      { label: 'Live profit tracking', top: '15%', left: '2%', align: 'right' },
      { label: 'Project risk indicators', top: '62%', right: '2%', align: 'left' },
      { label: 'Quick action shortcuts', top: '40%', left: '2%', align: 'right' },
    ],
  },
  {
    name: 'Deal Analyzer',
    url: 'app.flipperpro.com/flip-analyzer',
    Component: FlipAnalyzerMockup,
    annotations: [
      { label: 'Instant ROI calculation', top: '20%', right: '2%', align: 'left' },
      { label: 'Max Allowable Offer', top: '55%', right: '2%', align: 'left' },
      { label: 'Sensitivity analysis', top: '78%', left: '2%', align: 'right' },
    ],
  },
  {
    name: 'Lead Pipeline',
    url: 'app.flipperpro.com/leads',
    Component: KanbanMockup,
    annotations: [
      { label: 'Drag to reassign stage', top: '22%', left: '2%', align: 'right' },
      { label: 'Pipeline value tracking', top: '75%', right: '2%', align: 'left' },
      { label: 'Approval workflows', top: '45%', right: '2%', align: 'left' },
    ],
  },
];

export default function HeroMockup() {
  const [activePreview, setActivePreview] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActivePreview((p) => (p + 1) % previews.length);
        setIsTransitioning(false);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const current = previews[activePreview];
  const PreviewComponent = current.Component;

  return (
    <div className="mt-14 relative max-w-5xl mx-auto">
      {/* Bottom fade */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent z-10 pointer-events-none" />

      <div className="bg-slate-800/40 rounded-2xl border border-white/[0.08] p-1.5 glow relative">
        {/* Floating annotations (hidden on mobile) */}
        <div className="hidden lg:block">
          {current.annotations.map((ann, i) => (
            <div
              key={`${activePreview}-${i}`}
              className="absolute z-20 pointer-events-none"
              style={{
                top: ann.top,
                left: ann.left,
                right: ann.right,
                opacity: isTransitioning ? 0 : 1,
                transform: isTransitioning ? 'translateY(8px)' : 'translateY(0)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
                transitionDelay: `${i * 150}ms`,
              }}
            >
              <div
                className={`flex items-center gap-2 ${ann.align === 'left' ? 'flex-row' : 'flex-row-reverse'}`}
              >
                <div
                  className={`w-6 h-px ${ann.align === 'left' ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} from-violet-500/60 to-transparent`}
                />
                <div className="px-3 py-1.5 bg-violet-500/90 backdrop-blur-sm rounded-full shadow-lg shadow-violet-500/20">
                  <span className="text-[10px] font-semibold text-white whitespace-nowrap">
                    {ann.label}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-slate-900 rounded-xl overflow-hidden">
          {/* Browser chrome */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06]">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
            <div className="flex-1 flex justify-center">
              <div className="px-4 py-1 bg-white/[0.05] rounded-md text-[11px] text-slate-500 font-medium transition-all duration-300">
                {current.url}
              </div>
            </div>
          </div>

          {/* Preview content */}
          <div className="p-5 bg-gradient-to-br from-slate-800/80 to-slate-900 relative min-h-[240px] md:min-h-[300px]">
            <div
              style={{
                opacity: isTransitioning ? 0 : 1,
                transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
              }}
            >
              <PreviewComponent size="lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation dots */}
      <div className="flex items-center justify-center gap-3 mt-5 relative z-20">
        {previews.map((p, i) => (
          <button
            key={i}
            onClick={() => {
              setIsTransitioning(true);
              setTimeout(() => {
                setActivePreview(i);
                setIsTransitioning(false);
              }, 300);
            }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${
              i === activePreview
                ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            <div
              className={`w-1.5 h-1.5 rounded-full transition-all ${
                i === activePreview ? 'bg-violet-400 scale-125' : 'bg-slate-600'
              }`}
            />
            <span className="hidden sm:inline">{p.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
